import { useState, type ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Check, Copy, FileQuestionMark, LockKeyhole, RefreshCw, TriangleAlert, WifiOff } from 'lucide-react';
import { cx } from '@/lib/cx';
import { describeError, isApiError } from '@/lib/api';
import { Button, ButtonLink } from './Button';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  secondaryAction?: ReactNode;
  size?: 'sm' | 'md';
  className?: string;
}

/** Empty state: say what is missing and the one useful next step. Compact by default. */
export function EmptyState({ icon, title, description, action, secondaryAction, size = 'md', className }: EmptyStateProps) {
  return (
    <div className={cx('empty', `empty-${size}`, className)}>
      {icon && <span className="empty-icon" aria-hidden>{icon}</span>}
      <p className="empty-title">{title}</p>
      {description && <p className="empty-desc">{description}</p>}
      {(action || secondaryAction) && (
        <div className="empty-actions">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}

function RequestIdChip({ requestId }: { requestId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="request-id"
      onClick={() => {
        void navigator.clipboard?.writeText(requestId).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        });
      }}
      aria-label={`Copy request ID ${requestId}`}
    >
      <span className="request-id-label">Request ID</span>
      <code>{requestId.slice(0, 18)}</code>
      {copied ? <Check size={12} aria-hidden /> : <Copy size={12} aria-hidden />}
      <span className="sr-only" aria-live="polite">{copied ? 'Copied' : ''}</span>
    </button>
  );
}

export function ErrorState({ error, onRetry, title, size = 'md', className }: { error: unknown; onRetry?: () => void; title?: string; size?: 'sm' | 'md'; className?: string }) {
  const d = describeError(error);
  const offline = isApiError(error) && error.code === 'NETWORK_ERROR';
  const requestId = isApiError(error) ? error.requestId : null;
  return (
    <div className={cx('empty', 'empty-error', `empty-${size}`, className)} role="alert">
      <span className="empty-icon tone-danger" aria-hidden>
        {offline ? <WifiOff size={20} /> : <TriangleAlert size={20} />}
      </span>
      <p className="empty-title">{title ?? d.title}</p>
      <p className="empty-desc">{d.message}</p>
      <div className="empty-actions">
        {onRetry && (
          <Button size="sm" icon={<RefreshCw size={14} />} onClick={onRetry}>
            Try again
          </Button>
        )}
        {requestId && <RequestIdChip requestId={requestId} />}
      </div>
    </div>
  );
}

export function PermissionDenied({ message, backTo = '/' }: { message?: string; backTo?: string }) {
  return (
    <div className="empty empty-md empty-page">
      <span className="empty-icon" aria-hidden>
        <LockKeyhole size={20} />
      </span>
      <p className="empty-title">You don’t have access to this</p>
      <p className="empty-desc">{message ?? 'Your role does not include permission for this area. If you think this is a mistake, ask a workspace administrator.'}</p>
      <div className="empty-actions">
        <ButtonLink to={backTo} size="sm">
          Go back
        </ButtonLink>
      </div>
    </div>
  );
}

export function NotFound({ title = 'Page not found', message, backTo = '/', backLabel = 'Go to home' }: { title?: string; message?: string; backTo?: string; backLabel?: string }) {
  return (
    <div className="empty empty-md empty-page">
      <span className="empty-icon" aria-hidden>
        <FileQuestionMark size={20} />
      </span>
      <p className="empty-title">{title}</p>
      <p className="empty-desc">{message ?? 'It may have been moved, deleted, or you may not have access to it.'}</p>
      <div className="empty-actions">
        <ButtonLink to={backTo} size="sm">
          {backLabel}
        </ButtonLink>
      </div>
    </div>
  );
}

/**
 * Renders the right state for a react-query result: loading skeleton, 403 → PermissionDenied,
 * 404 → NotFound, other errors → ErrorState with retry, else children(data).
 */
export function QueryState<T>({ query, loading, children, notFound }: { query: UseQueryResult<T>; loading: ReactNode; children: (data: T) => ReactNode; notFound?: ReactNode }) {
  if (query.isPending) return <>{loading}</>;
  if (query.isError) {
    const e = query.error;
    if (isApiError(e) && e.status === 403) return <PermissionDenied message={e.message} />;
    if (isApiError(e) && e.status === 404) return <>{notFound ?? <NotFound />}</>;
    return <ErrorState error={e} onRetry={() => void query.refetch()} />;
  }
  return <>{children(query.data)}</>;
}
